import React, { useRef, useState } from "react";
import { CheckCircle2, Loader2, PhoneCall } from "lucide-react";
import { toast } from "./ui/sonner";
import { useApp } from "../context/AppContext";
import { newFollowupOperationKey } from "../lib/followupOperation";

/* 铃铛内联：公海按规则分配给我的客户 → 每位客户一键「已联系」（记录首次 / 最近联系时间 + 跟进记录，阶段不变）；全部联系后铃铛自动已读 */
export const PoolAssignedInline = ({ reminder, compact = false, onAllDone }) => {
  const { api, lang, user } = useApp();
  const zh = lang === "zh";
  const leads = reminder.leads || (reminder.leadIds || []).map((id) => ({ id, clientName: id }));
  const [done, setDone] = useState(new Set(reminder.contactedIds || []));
  const doneRef = useRef(new Set(reminder.contactedIds || []));
  const [busy, setBusy] = useState(null);
  // 同一铃铛和客户的失败重试保留操作键，不同客户各自独立；仅临时保存随机键，不保存客户资料。
  const pendingOperations = useRef(new Map());
  if (!leads.length) return null;
  const mark = async (e, lead) => {
    e.stopPropagation();
    setBusy(lead.id);
    const operationScope = JSON.stringify([user?.username || "", reminder.id, lead.id]);
    const storageKey = `crm.mark-contacted.${operationScope}`;
    let savedKey;
    try { savedKey = globalThis.sessionStorage?.getItem(storageKey); } catch { /* 存储不可用时仍可在当前组件内重试。 */ }
    const idempotencyKey = pendingOperations.current.get(operationScope)
      || (/^[A-Za-z0-9_-]{8,100}$/.test(savedKey || "") ? savedKey : newFollowupOperationKey());
    pendingOperations.current.set(operationScope, idempotencyKey);
    try { globalThis.sessionStorage?.setItem(storageKey, idempotencyKey); } catch { /* 浏览器禁用存储时降级到当前组件。 */ }
    const clearOperation = () => {
      pendingOperations.current.delete(operationScope);
      try { globalThis.sessionStorage?.removeItem(storageKey); } catch { /* 不影响已完成的操作。 */ }
    };
    try {
      const { data } = await api.post(`/crm/leads/${lead.id}/mark-contacted`, { reminderId: reminder.id, idempotencyKey });
      clearOperation();
      const next = new Set([...doneRef.current, ...(data.contactedIds || []), lead.id]);
      doneRef.current = next;
      setDone(next);
      // 顶栏提醒默认一分钟轮询；成功后立即同步，避免重新打开仍看到旧按钮。
      window.dispatchEvent(new Event("reminders-changed"));
      toast.success(zh ? `${lead.clientName} 已标记为已联系` : `${lead.clientName} marked contacted`);
      if (data.allDone || next.size >= leads.length) onAllDone?.();
    } catch (err) {
      // 只有服务端明确确认候选未生效，才能换键；权限变化和断线都可能发生在保存之后。
      const detail = err?.response?.data?.detail;
      if (detail?.code === "private_followup_unconfirmed_conflict") clearOperation();
      toast.error((typeof detail === "string" ? detail : detail?.message) || err.message);
    }
    setBusy(null);
  };
  return (
    <div className="flex flex-wrap gap-1" data-testid={`pool-assigned-inline-${reminder.id}`} data-done={done.size} data-total={leads.length} onClick={(e) => e.stopPropagation()}>
      {leads.map((l) => done.has(l.id) ? (
        <span key={l.id} className={`inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 ${compact ? "text-[10px] px-1.5 py-0.5" : "text-[11px] px-2 py-0.5"}`} data-testid={`pool-assigned-done-${l.id}`}><CheckCircle2 className="h-3 w-3" />{l.clientName}</span>
      ) : (
        <button key={l.id} type="button" disabled={!!busy} onClick={(e) => mark(e, l)} title={zh ? "标记为已联系（记录联系时间 + 跟进）" : "Mark contacted"}
          className={`inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white text-slate-700 hover:border-emerald-300 hover:bg-emerald-50 disabled:opacity-50 ${compact ? "text-[10px] px-1.5 py-0.5" : "text-[11px] px-2 py-0.5"}`} data-testid={`pool-assigned-mark-${l.id}`}>
          {busy === l.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <PhoneCall className="h-3 w-3 text-slate-400" />}{l.clientName}<span className="text-emerald-700 font-semibold">{zh ? "· 已联系" : "· contacted"}</span>
        </button>
      ))}
    </div>
  );
};
